import clsx from "clsx";
import React, { Children } from "react";
import { StickerType } from "web/react/emo/sticker";
import { container, divider } from "web/react/emo/utilities.css";
import { View } from "web/react/emo/view";
import { gettext } from "web/script/modules/django-i18n";
import { Schemas } from "web/types/examples/schemas";
import {
    BuyOptionSerializer,
    ProductSizePickerSerializer,
    BuyBuyBuyAreaSize as SizeOption,
    SizeSerializer,
} from "web/types/serializers";
import {
    BuyOptionPromotionSerializer,
    InStockProductPageContextProps,
    ShippingInformation,
} from "./in-stock-product-page.context";
import styles from "./in-stock-product-page.module.css";

export const getCostForPaidShipping = (
    formattedThresholdPrice: string,
    price: number | string
): string =>
    !!formattedThresholdPrice && formattedThresholdPrice.length > 0
        ? gettext("product.buy_area.excluding_shipping_free_over.label", {
              shipping_cost_format: price,
              free_shipping_threshold: formattedThresholdPrice,
          })
        : gettext("product.buy_area.excluding_shipping_info.label", {
              shipping_cost_format: price,
          });

export function getSizePrice(
    defaultSize: SizeSerializer
): InStockProductPageContextProps["activeProduct"]["price"] {
    const {
        formatted_price: fullPrice,
        formatted_sale_price: salePrice,
        is_on_sale: isOnSale,
        stock_status: stockStatus,
    } = defaultSize;

    return {
        fullPrice,
        inStock: stockStatus > 1,
        salePrice: isOnSale ? salePrice : undefined,
    };
}

export function extractProductSizeData(productSizesData: ProductSizePickerSerializer | null): {
    sizes?: SizeOption[];
    sizeList?: string[];
    userDefaultSchema: string;
    schemaLabels: Schemas;
} {
    const sizes = productSizesData?.sizes;
    const sizeList = sizes?.filter((size) => size?.in_stock).map((size) => size.human_size);

    const sizeData = {
        sizes,
        sizeList,
        userDefaultSchema: productSizesData?.user_default_schema as string,
        schemaLabels: productSizesData?.schema_labels as any,
    };

    if (sizes) {
        // Sort alphanumerically
        sizes.sort((a, b) => {
            const aSize = a.human_size.replace(/[^0-9.]/g, "");
            const bSize = b.human_size.replace(/[^0-9.]/g, "");
            return parseFloat(aSize) - parseFloat(bSize);
        });
    }

    return sizeData;
}

export function getDefaultSizeList(activeBuyOptionSizes): string[] {
    return Object.keys(activeBuyOptionSizes)
        .reduce((sizeList: string[], size) => {
            const isInStock = activeBuyOptionSizes[size].stock_status > 1;
            return isInStock ? [...sizeList, activeBuyOptionSizes[size].label] : sizeList;
        }, [])
        .sort();
}

export function getShippingInformation(
    activeBuyOption: BuyOptionSerializer,
    defaultSize: SizeSerializer
): ShippingInformation[] {
    const { extra_shipping: extraShipping } = activeBuyOption;
    const { shipping_info: shippingInfo } = defaultSize;

    const shippingInformation: ShippingInformation[] = [];
    const cheapest = shippingInfo?.cheapest_guaranteed || shippingInfo?.cheapest;

    if (cheapest) {
        const {
            formatted_price: price,
            is_free: isFreeShipping,
            formatted_threshold: formattedThreshold,
        } = cheapest;

        const shippingCost = isFreeShipping
            ? gettext("general.free_shipping")
            : getCostForPaidShipping(formattedThreshold || "", price);
        // to avoid passing other falsy values

        shippingInformation.push({
            info: shippingCost,
            isFree: isFreeShipping,
        });
    }

    const estimatedDelivery = extraShipping?.shipping_options?.[0]?.estimated_delivery;
    if (estimatedDelivery) {
        shippingInformation.push({
            info: estimatedDelivery,
            isFree: true,
        });
    }

    return shippingInformation;
}

export function getPromotion(
    activeBuyOption: BuyOptionSerializer,
    termsUrl: string
): BuyOptionPromotionSerializer | null {
    const {
        sizes: activeBuyOptionSizes,
        default_size: defaultActiveSize,
        retailer_name: retailerName,
    } = activeBuyOption;

    const defaultSize: SizeSerializer = activeBuyOptionSizes[defaultActiveSize];
    const promo = defaultSize.promotion;
    const retailerTerms = promo?.terms_url || defaultSize.lead_url;

    return (
        promo && {
            tcs: [
                {
                    // "Lyst promotion terms apply"
                    message: gettext("in_stock_product.lyst_promotion_terms_link"),
                    url: termsUrl,
                },
                {
                    // "{retailer_name} promotion terms apply"
                    message: gettext("in_stock_product.retailer_promotion_terms_link", {
                        retailer_name: retailerName,
                    }),
                    url: retailerTerms,
                },
            ],
            message: promo.html_message,
            promoCode: promo.promo_code,
            url: promo.url,
        }
    );
}

export const DEFAULT_PRODUCT_CUSTOMER_CAPTURE_DATA = {
    privacyPolicyLink: "",
    termsAndConditionsLink: "",
    cookiePolicyLink: "",
    signInLink: "",
    registerLink: "",
    shortPolicy: false,
    requiresOptIn: false,
};

export const DEFAULT_PRODUCT = {
    product_id: "",
    link_ids: [],
    canonical_slug: "",
    canonical_url: "",
    short_description: "",
    long_description: "",
    more_descriptions: {
        descriptions: [],
    },
    raw_color: null,
    color: null,
    designer: "",
    images: [],
    picker_thumbnail_url: "",
    product_sizes: null,
    lead_url: null,
    retailer_name: null,
    stock_availability_txt: null,
    descriptions: [],
};
export const DEFAULT_SIZE = {
    size_key: "",
    stock_status: 0,
    sale_status: 0,
    price: {},
    sale_price: {},
    formatted_price: "",
    formatted_sale_price: "",
    label: "",
    is_on_sale: false,
    shipping_info: null,
    promotion: null,
    lead_url: "",
};
export const DEFAULT_ACTIVE_BUY_OPTION = {
    link_id: "",
    product_id: "",
    product_code: null,
    date_updated: "",
    oos_since: null,
    date_updated_human: "",
    oos_since_human: null,
    is_amazon: false,
    in_stock: false,
    sizes: [],
    default_size: "",
    designer_slug: "",
    retailer_name: "",
    retailer_slug: "",
    retailer_network: "",
    retailer_checkout_enabled: false,
    extra_shipping: {
        retailer_name: "",
        shipping_options: [],
        returns_period: null,
        returns_price: null,
        returns_method: null,
    },
};
export const DEFAULT_FOOTER = {
    shop_gender_women_link: "",
    women_links: [],
    women_links_enabled: false,
    shop_gender_men_link: "",
    men_links: [],
    men_links_enabled: false,
    extra_links: [],
    extra_links_enabled: false,
    app_store_url: "",
    copyright_notice: "",
    app_callout_link: "",
    i18n_links: [],
};

export function PDPContent({ children }: { children: React.ReactNode }): React.ReactElement {
    return (
        <View className={styles.wrapper}>
            {Children.map(children as React.ReactElement[], (child, index) => {
                const childIsNotLast = index !== Children.count(children) - 1;
                return (
                    <>
                        {child}
                        {childIsNotLast && (
                            <View
                                className={clsx(divider, container)}
                                marginY={{ sm: "md", lg: "lg" }}
                            />
                        )}
                    </>
                );
            })}
        </View>
    );
}

export function getBuyOptionStickerType(
    buyOption: BuyOptionSerializer,
    cheapestOptionKey: string
): StickerType | undefined {
    const isCheapestPrice = cheapestOptionKey === buyOption.link_id;
    const isDesignerRetailer =
        buyOption.retailer_network.toLowerCase() === buyOption.designer_slug.toLowerCase();
    const isCheckout = buyOption.retailer_checkout_enabled;

    if (isCheapestPrice) {
        return "lowestPrice";
    }

    if (isDesignerRetailer) {
        return "officialStore";
    }

    if (isCheckout) {
        return "checkoutOnLyst";
    }

    return undefined;
}
